import type { ApolloClient } from "@apollo/client";
import type { Dispatch } from "redux";

import { push } from "store/actions/router";
import { get } from "lodash";

import { userConstants } from "./types";
import { userService } from "services/userService";
import ActionCreator from "utils/actionHandler";
import type { AppDispatch } from "store/configureStore";

export const registerUserRequest = (
  client: ApolloClient<object>,
  user: Parameters<typeof userService.register>[1]
) => {
  return (dispatch: AppDispatch) => {
    const actionCreator = new ActionCreator<typeof user>(
      userConstants.REGISTER_USER
    );
    dispatch(actionCreator.request());

    void userService.register(client, user).then(
      (data) => {
        dispatch(actionCreator.success(user));
        if (data != undefined && data.signUp.id != undefined) {
          dispatch(push(`/login/${window.location.search}`));
        }
        window.analytics.track("Individual Login Created");
      },
      (error: Error) => {
        window.analytics.track("Individual Login Failed");
        console.log("registerUserRequest Error: " + String(error));
        dispatch(actionCreator.failure(error));
      }
    );
  };
};
export const registerIndividualUser = (
  client: ApolloClient<object>,
  user: Parameters<typeof userService.registerIndividualUser>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<typeof user>(
      userConstants.REGISTER_OAUTH_USER
    );
    dispatch(actionCreator.request());

    return userService.registerIndividualUser(client, user).then(
      () => {
        dispatch(actionCreator.success(user));

        window.analytics.track("OAuth User Created");
      },
      (error: Error) => {
        window.analytics.track("Individual Login Failed");
        dispatch(actionCreator.failure(error));
        throw error;
      }
    );
  };
};

export const registerAdminUserRequest = (
  client: ApolloClient<object>,
  user: Parameters<typeof userService.registerAdmin>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<typeof user>(
      userConstants.REGISTER_ADMIN_USER
    );
    dispatch(actionCreator.request());

    void userService.registerAdmin(client, user).then(
      () => {
        dispatch(actionCreator.success(user));
      },
      (error: Error) => {
        console.log("registerAdminUserRequest Error: " + String(error));
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

// get the state for the now authenticated user
export const getUserState = (
  client: ApolloClient<object>,
  optionalAuth0User?: Record<string, unknown>
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getState>>
    >(userConstants.USER_STATE);
    dispatch(actionCreator.request());

    return userService
      .getState(client)
      .then(
        // get the user object
        (data) => {
          if (data != undefined && data.user != undefined) {
            // pre-existing: setItem coerces these objects to
            // "[object Object]" — String() preserves that exact behavior
            // (recorded in the migration doc)
            // eslint-disable-next-line @typescript-eslint/no-base-to-string -- intentional: preserves the pre-existing coercion
            localStorage.setItem("user", String(data.user));

            if (optionalAuth0User != undefined) {
              // eslint-disable-next-line @typescript-eslint/no-base-to-string -- intentional: preserves the pre-existing coercion
              localStorage.setItem("auth0User", String(optionalAuth0User));
              // auth0User rides into the slice via the reducer's
              // `...data.user` spread — not part of the query type
              (data.user as { auth0User?: unknown }).auth0User =
                optionalAuth0User;
            }

            return dispatch(actionCreator.success(data));
          } else {
            console.log("getUserState Error: data undefined.");
            return dispatch(
              actionCreator.failure("getUserState Error: data undefined.")
            );
          }
        },
        (error: Error) => {
          console.log("getUserState Error: " + String(error));
          return dispatch(actionCreator.failure(error));
        }
      )
      .catch((error: Error) => dispatch(actionCreator.failure(error)));
  };
};

// for background polling: a failed refresh keeps the state already loaded, USER_STATE_FAILURE would clear it and
// PrivateRoute would replace the page with NoState until a reload
export const refreshUserState = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getState>>
    >(userConstants.USER_STATE);

    return userService.getState(client).then(
      (data) => {
        if (data?.user) dispatch(actionCreator.success(data));
      },
      (error: Error) => {
        console.log("refreshUserState Error: " + String(error));
      }
    );
  };
};

export const updateIraAccountIdentityInfo = (
  client: ApolloClient<object>,
  identityInfo: Parameters<typeof userService.updateIraAccountIdentity>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updateIraAccountIdentity>>
    >(userConstants.USER_ON_BOARDING_IDENTITY_UPDATE);
    dispatch(actionCreator.request());

    return userService.updateIraAccountIdentity(client, identityInfo).then(
      (data) => {
        dispatch(actionCreator.success(data));
        if (data != undefined) {
          window.analytics.track("IRA Account On-Boarding: Identity Info");
        }
        return data;
      },
      (error: Error) => {
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const updateIraAccountUserCitizenship = (
  client: ApolloClient<object>,
  userCitizenship: Parameters<
    typeof userService.updateIraAccountUserCitizenship
  >[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updateIraAccountUserCitizenship>>
    >(userConstants.USER_ON_BOARDING_CITIZENSHIP_UPDATE);
    dispatch(actionCreator.request());

    return userService
      .updateIraAccountUserCitizenship(client, userCitizenship)
      .then(
        (data) => {
          dispatch(actionCreator.success(data));
          if (data != undefined) {
            window.analytics.track("IRA Account On-Boarding: Identity Info");
          }
          return data;
        },
        (error: Error) => {
          dispatch(actionCreator.failure(error));
        }
      );
  };
};

export const updateIraAccountTrustedContact = (
  client: ApolloClient<object>,
  trustedContact: Parameters<
    typeof userService.updateIraAccountTrustedContact
  >[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updateIraAccountTrustedContact>>
    >(userConstants.USER_ON_BOARDING_TRUSTED_CONTACT_UPDATE);
    dispatch(actionCreator.request());

    return userService
      .updateIraAccountTrustedContact(client, trustedContact)
      .then(
        (data) => {
          dispatch(actionCreator.success(data));
          if (data != undefined) {
            window.analytics.track("IRA Account On-Boarding: Trusted Contact");
          }
          return data;
        },
        (error: Error) => {
          dispatch(actionCreator.failure(error));
        }
      );
  };
};

export const updateIraAccountEmploymentStatus = (
  client: ApolloClient<object>,
  employmentStatus: Parameters<
    typeof userService.updateIraAccountEmploymentStatus
  >[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updateIraAccountEmploymentStatus>>
    >(userConstants.USER_ON_BOARDING_EMPLOYMENT_STATUS_UPDATE);
    dispatch(actionCreator.request());

    return userService
      .updateIraAccountEmploymentStatus(client, employmentStatus)
      .then(
        (data) => {
          dispatch(actionCreator.success(data));
          if (data != undefined) {
            window.analytics.track(
              "IRA Account On-Boarding: Employment Status"
            );
          }
          return data;
        },
        (error: Error) => {
          dispatch(actionCreator.failure(error));
        }
      );
  };
};

export const updateIraAccountEmploymentPosition = (
  client: ApolloClient<object>,
  employmentPosition: Parameters<
    typeof userService.updateIraAccountEmploymentPosition
  >[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updateIraAccountEmploymentPosition>>
    >(userConstants.USER_ON_BOARDING_EMPLOYMENT_POSITION_UPDATE);
    dispatch(actionCreator.request());

    return userService
      .updateIraAccountEmploymentPosition(client, employmentPosition)
      .then(
        (data) => {
          dispatch(actionCreator.success(data));
          if (data != undefined) {
            window.analytics.track(
              "IRA Account On-Boarding: Employment Position"
            );
          }
          return data;
        },
        (error: Error) => {
          dispatch(actionCreator.failure(error));
        }
      );
  };
};

export const updateIraAccountDisclosure = (
  client: ApolloClient<object>,
  disclosure: Parameters<typeof userService.updateIraAccountDisclosure>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updateIraAccountDisclosure>>
    >(userConstants.USER_ON_BOARDING_DISCLOSURE_UPDATE);
    dispatch(actionCreator.request());

    return userService.updateIraAccountDisclosure(client, disclosure).then(
      (data) => {
        dispatch(actionCreator.success(data));
        if (data != undefined) {
          window.analytics.track("IRA Account On-Boarding: Disclosure");
        }
      },
      (error: Error) => {
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const updateIraAccountInvestmentProfile = (
  client: ApolloClient<object>,
  investmentProfile: Parameters<
    typeof userService.updateIraAccountInvestmentProfile
  >[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updateIraAccountInvestmentProfile>>
    >(userConstants.USER_ON_BOARDING_INVESTMENT_PROFILE_UPDATE);
    dispatch(actionCreator.request());

    return userService
      .updateIraAccountInvestmentProfile(client, investmentProfile)
      .then(
        (data) => {
          dispatch(actionCreator.success(data));
          if (data != undefined) {
            window.analytics.track(
              "IRA Account On-Boarding: Investment Profile"
            );
          }
        },
        (error: Error) => {
          dispatch(actionCreator.failure(error));
        }
      );
  };
};

export const updateIraAccountSuitability = (
  client: ApolloClient<object>,
  suitability: Parameters<typeof userService.updateIraAccountSuitability>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updateIraAccountSuitability>>
    >(userConstants.USER_ON_BOARDING_INVESTMENT_SUITABILITY_UPDATE);
    dispatch(actionCreator.request());

    return userService.updateIraAccountSuitability(client, suitability).then(
      (data) => {
        dispatch(actionCreator.success(data));
        if (data != undefined) {
          window.analytics.track(
            "IRA Account On-Boarding: Investment Suitability"
          );
        }
      },
      (error: Error) => {
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const updateIraAccountTermsAndSubmit = (
  client: ApolloClient<object>,
  terms: Parameters<typeof userService.updateIraAccountTermsAndSubmit>[1]
) => {
  return (dispatch: AppDispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updateIraAccountTermsAndSubmit>>
    >(userConstants.USER_ON_BOARDING_INVESTMENT_TERMS_UPDATE);
    dispatch(actionCreator.request());

    return userService.updateIraAccountTermsAndSubmit(client, terms).then(
      (data) => {
        dispatch(actionCreator.success(data));
        if (data != undefined) {
          window.analytics.track("IRA Account On-Boarding: Terms and Submit");
          dispatch(push("/individual/thanks"));
        }
      },
      (error: Error) => {
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const updateEmployerAccountTermsAndSubmit = (
  client: ApolloClient<object>,
  terms: Parameters<typeof userService.updateEmployerAccountTermsAndSubmit>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<
        ReturnType<typeof userService.updateEmployerAccountTermsAndSubmit>
      >
    >(userConstants.USER_EMPLOYER_TERMS_UPDATE);
    dispatch(actionCreator.request());

    return userService.updateEmployerAccountTermsAndSubmit(client, terms).then(
      (data) => {
        if (data != undefined) {
          window.analytics.track(
            "Employer Account On-Boarding: Terms and Submit"
          );
        }
        return dispatch(actionCreator.success(data));
      },
      (error: Error) => dispatch(actionCreator.failure(error))
    );
  };
};

export const logoutUser = (userId: string) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<string>(userConstants.USER_LOGOUT);
    dispatch(actionCreator.request());

    void userService.logout(userId).then(
      () => {
        dispatch(actionCreator.success(userId));
      },
      (error: Error) => {
        console.log("logout Error: " + String(error));
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const persistDocument = ({
  file,
  docType,
  subDocType,
  metadata,
  documentFace,
}: {
  file: { fileUrl: string; file?: { type?: string; name?: string } };
  docType?: string;
  subDocType?: string;
  metadata?: object;
  documentFace?: string;
}) => {
  return (dispatch: Dispatch) => {
    const documentObject = {
      docUrl: file.fileUrl,
      docType,
      subDocType,
      metadata,
      documentFace,
      contentType: get(file, "file.type", "application/binary"),
      name: get(file, "file.name", "missing_file_name"),
    };
    const actionCreator = new ActionCreator<typeof documentObject>(
      userConstants.USER_UPLOADED_DOCUMENT
    );
    dispatch(actionCreator.request());

    dispatch(actionCreator.success(documentObject));
  };
};

export const confirmEmail = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<void>(userConstants.CONFIRM_EMAIL);
    dispatch(actionCreator.request());

    void userService.confirmEmail(client).then(
      () => {
        dispatch(actionCreator.success());
      },
      (error: Error) => {
        console.log("confirmEmail Error: " + String(error));
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export function setInitialUserLoading(isLoading: boolean) {
  return { type: userConstants.SET_INITIAL_USER_LOADING, isLoading };
}

export const getUserProfile = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getUserProfile>>
    >(userConstants.USER_PROFILE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.getUserProfile(client)
    );
  };
};

export const updateUserProfile = (
  client: ApolloClient<object>,
  userProfile: Parameters<typeof userService.updateUserProfile>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updateUserProfile>>
    >(userConstants.USER_PROFILE_UPDATE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.updateUserProfile(client, userProfile),
      { user: userProfile }
    );
  };
};

// Unused by design — the icon-api-service workflow owns this transition, and a
// client call races it. Do not wire this back up.
export const completeUserKyc = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.userCompletedKyc>>
    >(userConstants.USER_KYC_COMPLETED);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.userCompletedKyc(client)
    );
  };
};

export const competeUserOnboarding = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.userCompletedOnboarding>>
    >(userConstants.USER_ON_BOARDING_COMPLETED);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.userCompletedOnboarding(client)
    );
  };
};

export const submitYellowPathDocs = (
  client: ApolloClient<object>,
  documents: Parameters<typeof userService.updateIraYellowPathDocuments>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updateIraYellowPathDocuments>>
    >(userConstants.USER_SUBMIT_YELLOWPATH_DOCS);
    dispatch(actionCreator.request());
    return userService.updateIraYellowPathDocuments(client, documents).then(
      (data) => {
        dispatch(actionCreator.success(data));
        window.analytics.track("Submitted Yellow Path Docs");
      },
      (error: Error) => {
        console.log("submitYellowPathDocs Error: " + String(error));
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const getNeededYellowPathDocs = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getNeededYellowPathDocs>>
    >(userConstants.USER_NEEDED_YELLOWPATH_DOCS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.getNeededYellowPathDocs(client)
    );
  };
};

export const closeAccount = (
  client: ApolloClient<object>,
  accountId: string,
  reason: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.closeUserAccount>>
    >(userConstants.USER_CLOSE_ACCOUNT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.closeUserAccount(client, accountId, reason)
    );
  };
};

export const closeAndLiquidateAccountWithDisbursement = (
  client: ApolloClient<object>,
  input: Parameters<
    typeof userService.closeAndLiquidateAccountWithDisbursement
  >[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<
        ReturnType<typeof userService.closeAndLiquidateAccountWithDisbursement>
      >
    >(userConstants.USER_CLOSE_ACCOUNT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.closeAndLiquidateAccountWithDisbursement(client, input)
    );
  };
};

export const closeAndLiquidateAccount = (
  client: ApolloClient<object>,
  params: Parameters<typeof userService.closeAndLiquidateAccount>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.closeAndLiquidateAccount>>
    >(userConstants.USER_CLOSE_ACCOUNT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.closeAndLiquidateAccount(client, params)
    );
  };
};

export const changeEmail = (client: ApolloClient<object>, email: string) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.requestEmailChange>>
    >(userConstants.EMAIL_CHANGE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.requestEmailChange(client, email)
    );
  };
};

export const getEmailChangeRequest = (
  client: ApolloClient<object>,
  email: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getEmailChangeRequest>>
    >(userConstants.GET_EMAIL_CHANGE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.getEmailChangeRequest(client, email)
    );
  };
};

export const verifyEmailChangeRequest = (
  client: ApolloClient<object>,
  emailChangeRequestId: string,
  callback?: (
    data: Awaited<ReturnType<typeof userService.verifyEmailChangeRequest>>
  ) => void
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.verifyEmailChangeRequest>>
    >(userConstants.VERIFY_EMAIL_CHANGE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.verifyEmailChangeRequest(client, emailChangeRequestId),
      undefined,
      callback
    );
  };
};

export const deleteEmailChangeRequest = (
  client: ApolloClient<object>,
  emailChangeRequestId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.deleteEmailChangeRequest>>
    >(userConstants.DELETE_EMAIL_CHANGE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.deleteEmailChangeRequest(client, emailChangeRequestId)
    );
  };
};

export const getInvestmentProfileAndSuitability = (
  client: ApolloClient<object>,
  accountId: string
) => {
  return () => {
    return userService
      .getInvestmentProfileAndSuitability(client, accountId)
      .then((data) => data)
      .catch((error: Error) => {
        console.log(
          "getInvestmentProfileAndSuitability Error: " + String(error)
        );
        return {};
      });
  };
};

export const getInvestmentProfile = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getInvestmentProfile>>
    >(userConstants.GET_INVESTMENT_PROFILE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.getInvestmentProfile(client)
    );
  };
};

export const updateInvestmentProfileAndSuitability = (
  client: ApolloClient<object>,
  accountId: string,
  investmentProfile: Parameters<
    typeof userService.updateInvestmentProfileAndSuitability
  >[2],
  suitability: Parameters<
    typeof userService.updateInvestmentProfileAndSuitability
  >[3],
  isAccountScoped: boolean
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<
        ReturnType<typeof userService.updateInvestmentProfileAndSuitability>
      >
    >(userConstants.USER_INVESTMENT_PROFILE_UPDATE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.updateInvestmentProfileAndSuitability(
        client,
        accountId,
        investmentProfile,
        suitability,
        isAccountScoped
      )
    );
  };
};

export const acceptInvitation = (
  client: ApolloClient<object>,
  invitationCode: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.acceptInvitation>>
    >(userConstants.USER_ACCEPT_INVITATION);
    dispatch(actionCreator.request());
    return userService.acceptInvitation(client, invitationCode).then(
      (res) => {
        dispatch(actionCreator.success(res));
        return res;
      },
      (error: Error) => {
        console.log("acceptInvitation Error: " + String(error));
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const optOut = (
  client: ApolloClient<object>,
  inviteId: string,
  optedOut: boolean
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.optOut>>
    >(userConstants.OPT_OUT);
    dispatch(actionCreator.request());
    return userService.optOut(client, inviteId, optedOut).then(
      (res) => {
        dispatch(actionCreator.success(res));
        return res;
      },
      (error: Error) => {
        console.log("optOut Error: " + String(error));
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const getEmployerLinkRequests = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getEmployerLinkRequests>>
    >(userConstants.USER_EMPLOYER_LINK_REQUESTS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.getEmployerLinkRequests(client)
    );
  };
};

export const getLinkedEmployers = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getLinkedEmployers>>
    >(userConstants.USER_EMPLOYER_LINK);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.getLinkedEmployers(client)
    );
  };
};

export const linkPaychexPerksUser = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.linkPaychexPerksUser>>
    >(userConstants.LINK_PAYCHEX_PERKS_USER);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.linkPaychexPerksUser(client)
    );
  };
};

// Paychex: pull the 401k balances and link the roll-in. Only usable in the
// session the member arrives from the rollover tile — that login alone re-mints
// the Paychex access token this needs.
export const linkPaychexRolloverUser = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.linkPaychexRolloverUser>>
    >(userConstants.LINK_PAYCHEX_ROLLOVER_USER);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.linkPaychexRolloverUser(client)
    );
  };
};

// Paychex Flex (Perks): worker/employment records for the signed-in person.
export const getPaychexWorkerRecords = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getPaychexWorkerRecords>>
    >(userConstants.PAYCHEX_WORKER_RECORDS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.getPaychexWorkerRecords(client)
    );
  };
};

// Paychex Flex (Perks): fetch the full record for the selected worker. Resolves
// payrollFrequency (+ companyId) that the list query leaves null.
export const getPaychexWorkerRecord = (
  client: ApolloClient<object>,
  workerId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getPaychexWorkerRecord>>
    >(userConstants.PAYCHEX_WORKER_RECORD);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.getPaychexWorkerRecord(client, workerId)
    );
  };
};

// Paychex Flex (Perks): the person's active PAYCHECK_SPLIT contribution (v1: at
// most one), merged with its schedule — or null when none is set up. Mirrors how
// recurringContribution is read.
export const getPaycheckSplitContribution = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getPaycheckSplitContribution>>
    >(userConstants.GET_PAYCHECK_SPLIT_CONTRIBUTION);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.getPaycheckSplitContribution(client)
    );
  };
};

export const deleteEmployerLink = (
  client: ApolloClient<object>,
  linkId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<string>(
      userConstants.DELETE_USER_EMPLOYER_LINK
    );
    dispatch(actionCreator.request());
    return userService.deleteEmployerLink(client, linkId).then(
      () => {
        dispatch(actionCreator.success(linkId));
      },
      (error: Error) => {
        console.log("deleteEmployerLink Error: " + String(error));
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const startInvestmentProfileUpdate = (client: ApolloClient<object>) => {
  return async (dispatch: Dispatch) => {
    await userService.startInvestmentProfileUpdateRequest(client);
    return getUserState(client)(dispatch);
  };
};

export const startPortfolioUpdate = (client: ApolloClient<object>) => {
  return async (dispatch: Dispatch) => {
    await userService.startPortfolioUpdateRequest(client);
    return getUserState(client)(dispatch);
  };
};

export const exitInvestmentProfileUpdate = (client: ApolloClient<object>) => {
  return async (dispatch: Dispatch) => {
    await userService.exitInvestmentProfileUpdateRequest(client);
    return getUserState(client)(dispatch);
  };
};

export const deleteEmployerLinkRequest = (
  client: ApolloClient<object>,
  linkRequestId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.deleteEmployerLinkRequest>>
    >(userConstants.DELETE_USER_EMPLOYER_LINK_REQ);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.deleteEmployerLinkRequest(client, linkRequestId)
    );
  };
};

export const skipSsn = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.skipSsn>>
    >(userConstants.SKIP_SSN);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.skipSsn(client)
    );
  };
};

export const submitSsn = (client: ApolloClient<object>, ssn: string) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.submitSsn>>
    >(userConstants.SUBMIT_SSN);
    dispatch(actionCreator.request());
    return userService.submitSsn(client, ssn).then(
      (data) => {
        return dispatch(actionCreator.success(data));
      },
      (error: Error) => {
        console.log("Error submitting SSN: " + String(error));
        return dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const submitEin = (
  client: ApolloClient<object>,
  params: Parameters<typeof userService.submitEin>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.submitEin>>
    >(userConstants.SUBMIT_EIN);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.submitEin(client, params)
    );
  };
};

export const validateEin = (
  client: ApolloClient<object>,
  params: Parameters<typeof userService.validateEin>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.validateEin>>
    >(userConstants.VALIDATE_EIN);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.validateEin(client, params)
    );
  };
};

export const createSupportRequest = (
  client: ApolloClient<object>,
  params: Parameters<typeof userService.createSupportRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.createSupportRequest>>
    >(userConstants.CREATE_REQUEST);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.createSupportRequest(client, params)
    );
  };
};

export const clearUploadedDocuments = () => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<void>(
      userConstants.CLEAR_USER_UPLOADED_DOCUMENT
    );
    dispatch(actionCreator.request());
    dispatch(actionCreator.success());
  };
};

export const getRequiredUpdates = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getRequiredStepsRequest>>
    >(userConstants.GET_USER_REQUIRED_STEPS);
    const allCreators = async () => {
      const response = await userService.getRequiredStepsRequest(client);
      // eslint-disable-next-line @typescript-eslint/await-thenable -- pre-existing no-op: builds the getUserState thunk but never dispatches it; conversion preserves behavior
      await getUserState(client);
      return response;
    };
    return actionCreator.dispatchFullLifecycle(dispatch, allCreators());
  };
};

export const updateRequiredDisclosures = (
  client: ApolloClient<object>,
  data: Parameters<typeof userService.updateRequiredDisclosuresRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updateRequiredDisclosuresRequest>>
    >(userConstants.UPDATE_REQUIRED_DISCLOSURES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.updateRequiredDisclosuresRequest(client, data)
    );
  };
};

export const updateSelectedAccountType = (
  client: ApolloClient<object>,
  accountType: Parameters<typeof userService.updateSelectedAccountType>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updateSelectedAccountType>>
    >(userConstants.UPDATE_SELECTED_ACCOUNT_TYPE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.updateSelectedAccountType(client, accountType)
    );
  };
};

export const initializeRollover = (
  client: ApolloClient<object>,
  params: Parameters<typeof userService.initializeRollover>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.initializeRollover>>
    >(userConstants.INITIALIZE_ROLLOVER);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.initializeRollover(client, params)
    );
  };
};

// Not initializeRollover: that one also creates a rollover request and a letter
// of acceptance, neither of which a Paychex member should get — reconciliation
// supplies the rollover request later.
export const initializePaychexRollover = (
  client: ApolloClient<object>,
  params: Parameters<typeof userService.initializePaychexRollover>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.initializePaychexRollover>>
    >(userConstants.INITIALIZE_PAYCHEX_ROLLOVER);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.initializePaychexRollover(client, params)
    );
  };
};

export const submitRolloverAccountInfo = (
  client: ApolloClient<object>,
  params: Parameters<typeof userService.submitRolloverAccountInfo>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.submitRolloverAccountInfo>>
    >(userConstants.SUBMIT_ROLLOVER_INFO);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.submitRolloverAccountInfo(client, params)
    );
  };
};

export const submitUserRolloverInfo = (
  client: ApolloClient<object>,
  params: Parameters<typeof userService.submitUserRolloverInfo>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.submitUserRolloverInfo>>
    >(userConstants.SUBMIT_USER_ROLLOVER_INFO);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.submitUserRolloverInfo(client, params)
    );
  };
};

export const addRolloverAccountDisclosure = (
  client: ApolloClient<object>,
  disclosure: Parameters<typeof userService.addRolloverAccountDisclosure>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.addRolloverAccountDisclosure>>
    >(userConstants.ADD_ROLLOVER_ACCOUNT_DISCLOSURE);
    dispatch(actionCreator.request());

    return userService.addRolloverAccountDisclosure(client, disclosure).then(
      (data) => {
        dispatch(actionCreator.success(data));
        if (data != undefined) {
          window.analytics.track(
            "IRA Account On-Boarding: Rollover Disclosure"
          );
        }
      },
      (error: Error) => {
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const updateRolloverInvestmentProfileAndSuitability = (
  client: ApolloClient<object>,
  investmentProfile: Parameters<
    typeof userService.updateRolloverInvestmentProfileAndSuitability
  >[1],
  suitability: Parameters<
    typeof userService.updateRolloverInvestmentProfileAndSuitability
  >[2]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<
        ReturnType<
          typeof userService.updateRolloverInvestmentProfileAndSuitability
        >
      >
    >(userConstants.USER_ROLLOVER_INVESTMENT_PROFILE_UPDATE);
    dispatch(actionCreator.request());

    return userService
      .updateRolloverInvestmentProfileAndSuitability(
        client,
        investmentProfile,
        suitability
      )
      .then(
        (data) => {
          dispatch(actionCreator.success(data));
          if (data != undefined) {
            window.analytics.track(
              "IRA Account On-Boarding: Rollover Investment And Suitability"
            );
          }
        },
        (error: Error) => {
          dispatch(actionCreator.failure(error));
        }
      );
  };
};

export const updateRolloverAccountTermsAndSubmit = (
  client: ApolloClient<object>,
  terms: Parameters<typeof userService.updateRolloverAccountTermsAndSubmit>[1]
) => {
  return (dispatch: AppDispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<
        ReturnType<typeof userService.updateRolloverAccountTermsAndSubmit>
      >
    >(userConstants.UPDATE_ROLLOVER_ACCOUNT_TERMS_SUBMIT);
    dispatch(actionCreator.request());

    return userService.updateRolloverAccountTermsAndSubmit(client, terms).then(
      (data) => {
        dispatch(actionCreator.success(data));
        if (data != undefined) {
          window.analytics.track(
            "IRA Rollover Account On-Boarding: Terms and Submit"
          );
          dispatch(push("/individual/thanks"));
        }
      },
      (error: Error) => {
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const updatePaychexAccountTermsAndSubmit = (
  client: ApolloClient<object>,
  terms: Parameters<typeof userService.updatePaychexAccountTermsAndSubmit>[1]
) => {
  return (dispatch: AppDispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.updatePaychexAccountTermsAndSubmit>>
    >(userConstants.UPDATE_PAYCHEX_ACCOUNT_TERMS_SUBMIT);
    dispatch(actionCreator.request());

    return userService.updatePaychexAccountTermsAndSubmit(client, terms).then(
      (data) => {
        dispatch(actionCreator.success(data));
        if (data != undefined) {
          window.analytics.track("Paychex IRA On-Boarding: Terms and Submit");
          dispatch(push("/individual/thanks"));
        }
      },
      (error: Error) => {
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const getUserTermsSubmissionDate = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.userTermsDate>>
    >(userConstants.GET_USER_TERMS_DATE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.userTermsDate(client)
    );
  };
};

export const getRolloverInfo = (
  client: ApolloClient<object>,
  params: Parameters<typeof userService.getRolloverInfo>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getRolloverInfo>>
    >(userConstants.GET_ROLLOVER_INFO);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.getRolloverInfo(client, params)
    );
  };
};

export const cancelRollover = (
  client: ApolloClient<object>,
  accountId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<string>(
      userConstants.CANCEL_ROLLOVER
    );
    dispatch(actionCreator.request());
    return userService
      .cancelRollover(client, accountId)
      .then(() => dispatch(actionCreator.success(accountId)))
      .catch((error: Error) => dispatch(actionCreator.failure(error)));
  };
};

export const linkSecondaryAuth = (
  client: ApolloClient<object>,
  params: Parameters<typeof userService.linkSecondaryAuth>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.linkSecondaryAuth>>
    >(userConstants.LINK_SECONDARY_AUTH);
    dispatch(actionCreator.request());
    return userService.linkSecondaryAuth(client, params).then(
      (data) => {
        dispatch(actionCreator.success(data));
        window.analytics.track("Individual Secondary Auth Linked");
        return data;
      },
      (error: Error) => {
        dispatch(actionCreator.failure(error));
        throw error;
      }
    );
  };
};

export const createAndLinkSecondaryAuth = (
  client: ApolloClient<object>,
  params: Parameters<typeof userService.createAndLinkSecondaryAuth>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.createAndLinkSecondaryAuth>>
    >(userConstants.CREATE_AND_LINK_SECONDARY_AUTH);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.createAndLinkSecondaryAuth(client, params)
    );
  };
};

export const getUserAgreements = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.getUserAgreements>>
    >(userConstants.GET_USER_AGREEMENTS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.getUserAgreements(client)
    );
  };
};

export const createAuthLinkingSession = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userService.createAuthLinkingSession>>
    >(userConstants.CREATE_AUTH_LINKING_SESSION);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userService.createAuthLinkingSession(client)
    );
  };
};
